import React, { useState, useEffect } from 'react';
import ConfigPanel from './components/ConfigPanel';
import WidgetPreview from './components/WidgetPreview';
import EmbedCodeModal from './components/EmbedCodeModal';
import { BotConfig, VoiceName } from './types';

const App: React.FC = () => {
  const [searchParams] = useState(() => new URLSearchParams(window.location.search));
  const isEmbedMode = searchParams.get('mode') === 'embed';
  
  const [embedConfig, setEmbedConfig] = useState<BotConfig | null>(null);
  const [embedApiKey, setEmbedApiKey] = useState<string | undefined>(undefined);
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);

  // Default Config for Creator Mode
  const [config, setConfig] = useState<BotConfig>({
    name: 'Gemini Assistant',
    voice: VoiceName.Puck,
    systemInstruction: 'You are a friendly and helpful AI assistant. You answer concisely and cheerfully.',
    themeColor: '#4F46E5', // Indigo-600
    avatarUrl: 'https://picsum.photos/200',
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  // FORCE TRANSPARENCY IN EMBED MODE
  useEffect(() => {
    if (isEmbedMode) {
      document.documentElement.style.backgroundColor = 'transparent';
      document.body.style.backgroundColor = 'transparent';
      const root = document.getElementById('root');
      if (root) root.style.backgroundColor = 'transparent';
    }
  }, [isEmbedMode]);

  // HANDSHAKE PROTOCOL for Embed Mode
  useEffect(() => {
    if (isEmbedMode) {
      // 1. Tell parent "I'm ready for config"
      window.parent.postMessage({ type: 'GEMINI_BOT_LOADED' }, '*');

      // 2. Listen for config
      const handleMessage = (event: MessageEvent) => {
        if (event.data && event.data.type === 'GEMINI_BOT_CONFIG') {
          if (event.data.config) {
             console.log("Config received via message");
             setEmbedConfig(event.data.config);
             if (event.data.apiKey) setEmbedApiKey(event.data.apiKey);
             setIsConfigLoaded(true);
          }
        }
      };
      
      window.addEventListener('message', handleMessage);

      // 3. Fallback: If no config received (e.g. direct load), just load default after delay
      const timer = setTimeout(() => {
         if (!isConfigLoaded) {
             console.log("No config received, falling back to default");
             setIsConfigLoaded(true);
         }
      }, 1000);

      return () => {
        window.removeEventListener('message', handleMessage);
        clearTimeout(timer);
      };
    }
  }, [isEmbedMode, isConfigLoaded]);

  // RENDER: EMBED MODE (Standalone Widget)
  if (isEmbedMode) {
    if (!isConfigLoaded && !embedConfig) {
        // Show a spinner while waiting for config handshake
        return (
          <>
             <div className="h-full w-full flex items-end justify-end p-4 bg-transparent">
                <div className="w-12 h-12 rounded-full border-2 border-gray-300 border-t-indigo-600 animate-spin bg-white"></div>
             </div>
          </>
        );
    }

    const effectiveConfig = embedConfig || config;

    return (
      <>
        <style>{`
          html, body, #root { 
            background-color: transparent !important; 
            margin: 0;
            padding: 0;
            width: 100%;
            height: 100%;
            overflow: hidden !important; 
          }
        `}</style>
        <div className="h-full w-full overflow-hidden flex flex-col justify-end items-end">
          <WidgetPreview config={effectiveConfig} isEmbed={true} apiKey={embedApiKey} />
        </div>
      </>
    );
  }

  // RENDER: CREATOR MODE (Config + Preview)
  return (
    <div className="flex h-screen w-screen overflow-hidden bg-white">
      {/* Left: Configuration */}
      <ConfigPanel 
        config={config} 
        setConfig={setConfig} 
        onGenerateCode={() => setIsModalOpen(true)}
      />

      {/* Right: Live Preview */}
      <WidgetPreview config={config} />

      {/* Modal */}
      <EmbedCodeModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        config={config} 
      />
    </div>
  );
};

export default App;